import type { BlogPost } from "@shared/schema";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BlogListProps {
  posts: BlogPost[];
}

export function BlogList({ posts }: BlogListProps) {
  if (!posts.length) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No blog posts found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {posts.map((post) => (
        <BlogCard key={post.id} post={post} />
      ))}
    </div>
  );
}

interface BlogCardProps {
  post: BlogPost;
}

function BlogCard({ post }: BlogCardProps) {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Image (if available) */}
        {post.imageUrl && (
          <div className="md:col-span-1">
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-full object-cover aspect-video md:aspect-square"
            />
          </div>
        )}
        
        {/* Content */}
        <div className={post.imageUrl ? "md:col-span-2" : "md:col-span-3"}>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap gap-2 mb-2">
              <Badge variant="outline">{post.category}</Badge>
              {post.featured && <Badge variant="secondary">Featured</Badge>}
            </div>
            <h3 className="text-xl font-bold">{post.title}</h3>
          </CardHeader>
          
          <CardContent className="pb-2">
            <p className="text-muted-foreground line-clamp-3">{post.summary}</p>
          </CardContent>
          
          <CardFooter className="flex justify-between">
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 mr-1" />
              {new Date(post.publishedAt).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short", 
                day: "numeric"
              })}
            </div>
            
            <Button asChild variant="ghost" size="sm">
              <a href={`/blog/${post.slug}`}>
                Read More <ChevronRight className="h-4 w-4 ml-1" />
              </a>
            </Button>
          </CardFooter>
        </div>
      </div>
    </Card>
  );
}